import React from 'react';
import { Source, Layer } from 'react-map-gl';

const PathVisualizer = ({ 
  currentPath, 
  matchedPath, 
  mapMatchingStats,
  completedBlocks, 
  tracking, 
  transportMode, 
  showModeColors = false,
  showMatchedPath = true 
}) => {
  // Get color for transportation mode
  const getModeColor = (mode) => {
    const modeColors = {
      walking: '#ff4444',
      running: '#ff8800',
      biking: '#44aa44',
      driving: '#4444ff',
      transit: '#8844ff'
    };
    return modeColors[mode] || '#ff4444';
  };

  // Convert path to GeoJSON format
  const pathToGeoJSON = (path) => {
    if (!path || path.length < 2) return null;

    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: path.map(point => [point.longitude, point.latitude])
      },
      properties: {
        type: 'tracked-path',
        transportMode: transportMode || 'walking'
      }
    };
  };

  // Convert matched path to GeoJSON format
  const matchedPathToGeoJSON = (matchedPath) => {
    if (!matchedPath || matchedPath.length < 2) return null;

    // Create separate features for matched and unmatched segments
    const matchedSegments = [];
    const unmatchedSegments = [];
    let currentSegment = [];
    let isMatched = null;

    for (let i = 0; i < matchedPath.length; i++) {
      const point = matchedPath[i];
      const pointIsMatched = point.isMatched;

      if (isMatched === null) {
        isMatched = pointIsMatched;
        currentSegment.push([point.longitude, point.latitude]);
      } else if (isMatched === pointIsMatched) {
        currentSegment.push([point.longitude, point.latitude]);
      } else {
        // Segment type changed, save current segment and start new one
        if (currentSegment.length > 1) {
          if (isMatched) {
            matchedSegments.push(currentSegment);
          } else {
            unmatchedSegments.push(currentSegment);
          }
        }
        isMatched = pointIsMatched;
        currentSegment = [[point.longitude, point.latitude]];
      }
    }

    // Add the last segment
    if (currentSegment.length > 1) {
      if (isMatched) {
        matchedSegments.push(currentSegment);
      } else {
        unmatchedSegments.push(currentSegment);
      }
    }

    return {
      matched: matchedSegments.map(segment => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: segment
        },
        properties: {
          type: 'matched-path',
          transportMode: transportMode || 'walking'
        }
      })),
      unmatched: unmatchedSegments.map(segment => ({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: segment
        },
        properties: {
          type: 'unmatched-path',
          transportMode: transportMode || 'walking'
        }
      }))
    };
  };

  // Convert block to GeoJSON polygon
  const blockToGeoJSON = (block) => {
    if (!block.path || block.path.length < 3) return null;

    // Create a polygon from the path
    const coordinates = block.path.map(point => [point.longitude, point.latitude]);
    
    // Close the polygon by adding the first point at the end
    if (coordinates[0] !== coordinates[coordinates.length - 1]) {
      coordinates.push(coordinates[0]);
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates]
      },
      properties: {
        type: 'completed-block',
        shouldMarkArea: block.shouldMarkArea,
        unwalkedRoadsCount: block.unwalkedRoadsCount,
        totalDistance: block.totalDistance
      }
    };
  };

  // Current path GeoJSON
  const currentPathGeoJSON = pathToGeoJSON(currentPath);

  // Matched path GeoJSON
  const matchedPathGeoJSON = matchedPathToGeoJSON(matchedPath);

  // Completed blocks GeoJSON
  const completedBlocksGeoJSON = {
    type: 'FeatureCollection',
    features: completedBlocks
      .map(block => blockToGeoJSON(block))
      .filter(block => block !== null)
  };

  return (
    <>
      {/* Original GPS path (always shown, but with different opacity when matched path is shown) */}
      {currentPathGeoJSON && (
        <Source type="geojson" data={currentPathGeoJSON}>
          <Layer
            id="current-path"
            type="line"
            paint={{
              'line-color': showModeColors 
                ? getModeColor(transportMode || 'walking')
                : (tracking ? '#ff4444' : '#666666'),
              'line-width': showMatchedPath && matchedPath.length > 0 ? 2 : 4,
              'line-opacity': showMatchedPath && matchedPath.length > 0 ? 0.4 : 0.8,
              'line-dasharray': showMatchedPath && matchedPath.length > 0 ? [2, 2] : [1, 0]
            }}
          />
        </Source>
      )}

      {/* Matched road path (shown when available) */}
      {showMatchedPath && matchedPathGeoJSON && (
        <>
          {/* Matched segments */}
          {matchedPathGeoJSON.matched.length > 0 && (
            <Source type="geojson" data={{
              type: 'FeatureCollection',
              features: matchedPathGeoJSON.matched
            }}>
              <Layer
                id="matched-path"
                type="line"
                paint={{
                  'line-color': showModeColors 
                    ? getModeColor(transportMode || 'walking')
                    : '#00aa00',
                  'line-width': 4,
                  'line-opacity': 0.9
                }}
              />
            </Source>
          )}

          {/* Unmatched segments */}
          {matchedPathGeoJSON.unmatched.length > 0 && (
            <Source type="geojson" data={{
              type: 'FeatureCollection',
              features: matchedPathGeoJSON.unmatched
            }}>
              <Layer
                id="unmatched-path"
                type="line"
                paint={{
                  'line-color': '#ff6666',
                  'line-width': 3,
                  'line-opacity': 0.7,
                  'line-dasharray': [3, 3]
                }}
              />
            </Source>
          )}
        </>
      )}

      {/* Completed blocks */}
      {completedBlocksGeoJSON.features.length > 0 && (
        <Source type="geojson" data={completedBlocksGeoJSON}>
          {/* Block outlines */}
          <Layer
            id="block-outlines"
            type="line"
            paint={{
              'line-color': showModeColors 
                ? getModeColor(transportMode || 'walking')
                : '#ff4444',
              'line-width': 3,
              'line-opacity': 0.9
            }}
          />
          
          {/* Block fills (only for blocks that should be marked) */}
          <Layer
            id="block-fills"
            type="fill"
            paint={{
              'fill-color': [
                'case',
                ['get', 'shouldMarkArea'],
                showModeColors 
                  ? getModeColor(transportMode || 'walking')
                  : '#ff4444',
                'rgba(255, 68, 68, 0.1)'
              ],
              'fill-opacity': [
                'case',
                ['get', 'shouldMarkArea'],
                0.3,
                0.05
              ]
            }}
          />
        </Source>
      )}
    </>
  );
};

export default PathVisualizer;
